import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import PageHeader from '../components/PageHeader';
import { CONTACT } from '../mock';
import { ShoppingBag, Lock, AlertCircle, Store, Bike, Clock, Calendar, Gift, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { usePrepTime } from '../hooks/usePrepTime';

// Generate available time slots for today (15-min increments)
function getTimeSlots() {
  const now = new Date();
  const slots = [];
  // Start 30 min from now, rounded up to next 15
  const start = new Date(now.getTime() + 30 * 60 * 1000);
  start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
  // End of business hours today (11 PM weekdays, midnight Fri/Sat)
  const dow = now.getDay(); // 0=Sun, 5=Fri, 6=Sat
  const closeHour = (dow === 5 || dow === 6) ? 24 : 23;
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), closeHour, 0, 0);
  for (let t = new Date(start); t < end; t = new Date(t.getTime() + 15 * 60 * 1000)) {
    slots.push(new Date(t));
  }
  return slots;
}

export default function Checkout() {
  const { items, subtotal, gst, pst, tax, total, clear } = useCart();
  const { prep } = usePrepTime();
  const nav = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [orderType, setOrderType] = useState('pickup');
  const [when, setWhen] = useState('asap'); // asap | scheduled
  const [scheduledTime, setScheduledTime] = useState('');
  const [timeSlots] = useState(() => getTimeSlots()); // pickup | delivery
  const [form, setForm] = useState({
    customerName: '', customerPhone: '', customerEmail: '', notes: '',
    deliveryAddress: '', deliveryCity: 'Port Coquitlam', deliveryState: 'BC', deliveryPostal: '',
  });
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [serverTotals, setServerTotals] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  // Loyalty state
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [loyaltyError, setLoyaltyError] = useState('');
  const [referralCode, setReferralCode] = useState(() => localStorage.getItem('wrappus_ref') || '');

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { if (items.length === 0) nav('/menu'); }, [items, nav]);

  const deliveryFee = quote?.fee || 0;
  const loyaltyDiscount = pointsToRedeem >= 100 ? +(pointsToRedeem * 0.10).toFixed(2) : 0;
  const grandTotal = Math.max(0, +(total + deliveryFee - loyaltyDiscount).toFixed(2));
  const amountDue = serverTotals?.total ?? grandTotal;
  // Fetch loyalty balance when email is entered
  useEffect(() => {
    if (!form.customerEmail || !form.customerEmail.includes('@')) { setLoyaltyBalance(null); return; }
    const timer = setTimeout(() => {
      api.post('/loyalty/balance', { email: form.customerEmail })
        .then((r) => setLoyaltyBalance(r.data))
        .catch(() => setLoyaltyBalance(null));
    }, 600);
    return () => clearTimeout(timer);
  }, [form.customerEmail]);

  // Validate points redemption
  useEffect(() => {
    if (!pointsToRedeem || !form.customerEmail) { setLoyaltyError(''); return; }
    api.post('/loyalty/preview', { email: form.customerEmail, points: pointsToRedeem, subtotal })
      .then((r) => setLoyaltyError(r.data.valid ? '' : r.data.error))
      .catch(() => {});
  }, [pointsToRedeem, form.customerEmail, subtotal]);

  const fetchQuote = async () => {
    if (!form.deliveryAddress || !form.deliveryPostal) return;
    setQuoteLoading(true); setQuoteError(''); setQuote(null);
    try {
      const { data } = await api.post('/delivery/quote', {
        line1: form.deliveryAddress, city: form.deliveryCity, state: form.deliveryState, postal: form.deliveryPostal,
        scheduledFor: when === 'scheduled' ? scheduledTime : null,
      });
      setQuote(data);
    } catch (err) {
      setQuoteError(err?.response?.data?.detail || 'Could not fetch delivery quote for this address.');
    } finally {
      setQuoteLoading(false);
    }
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    if (when === 'scheduled' && !scheduledTime) {
      toast({ title: 'Please pick a time' });
      return;
    }
    if (orderType === 'delivery' && !quote?.quoteId) {
      toast({ title: 'Please get a delivery quote first' });
      return;
    }
    try {
      const payload = {
        items: items.map((i) => ({ menuItemId: i.menuItemId || i.id, name: i.name, price: i.price, qty: i.qty, modifiers: i.modifiers || [] })),
        subtotal, tax, total: grandTotal, type: orderType,
        customerName: form.customerName, customerPhone: form.customerPhone,
        customerEmail: form.customerEmail, notes: form.notes,
        scheduledFor: when === 'scheduled' ? scheduledTime : null,
        pointsToRedeem: pointsToRedeem >= 100 ? pointsToRedeem : 0,
        loyaltyDiscount,
        referralCode: referralCode || undefined,
        ...(orderType === 'delivery' ? {
          deliveryAddress: form.deliveryAddress, deliveryCity: form.deliveryCity,
          deliveryState: form.deliveryState, deliveryPostal: form.deliveryPostal,
          deliveryQuoteId: quote.quoteId, deliveryFee: quote.fee,
        } : {}),
      };
      const { data } = await api.post('/orders', payload);
      setOrderId(data.orderId);
      setServerTotals(data);
      localStorage.setItem('wrappus_pending_order', data.orderId);
      setStep(2);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not create order' });
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (paying || !orderId) return;
    setPaying(true); setPayError('');
    try {
      const { data } = await api.post('/payments/checkout', {
        orderId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setPayError(err?.response?.data?.detail || 'Could not start the payment. Please try again.');
      setPaying(false);
    }
  };

  return (
    <main>
      <PageHeader title="Checkout" breadcrumb="Checkout" />
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_380px] gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              {[1, 2].map((n) => (
                <div key={n} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= n ? 'bg-[#ED2E2E] text-white' : 'bg-gray-100 text-gray-500'}`}>{n}</div>
                  <span className={`text-sm font-semibold ${step >= n ? 'text-[#0F1C2E]' : 'text-gray-400'}`}>{n === 1 ? 'Your Details' : 'Payment'}</span>
                  {n === 1 && <div className={`w-16 h-px ${step >= 2 ? 'bg-[#ED2E2E]' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>

            {step === 1 && (
              <form onSubmit={submitDetails} className="bg-[#FDF3EC] rounded-3xl p-8 space-y-5">
                {prep?.rushActive && orderType === 'pickup' && (
                  <div className="flex items-start gap-2 rounded-2xl bg-white border border-[#F5A623]/50 px-4 py-3 text-sm text-[#0F1C2E]" data-testid="prep-busy-note">
                    <Clock size={16} className="text-[#F5A623] shrink-0 mt-0.5" />
                    <span>{prep.message}</span>
                  </div>
                )}
                {/* Order type toggle */}
                <div>
                  <label className="text-sm font-semibold text-[#0F1C2E] mb-3 block">Choose Order Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setOrderType('pickup')} className={`p-4 rounded-2xl border-2 text-left transition ${orderType === 'pickup' ? 'border-[#ED2E2E] bg-white shadow-md' : 'border-gray-200 bg-white/50 hover:border-[#ED2E2E]/50'}`}>
                      <Store className={`mb-2 ${orderType === 'pickup' ? 'text-[#ED2E2E]' : 'text-gray-500'}`} size={22} />
                      <div className="font-bold text-[#0F1C2E]">Pickup</div>
                      <div className="text-xs text-gray-500 mt-1">Ready in ~{prep?.etaMinutes ?? 10} min. No delivery fee.</div>
                    </button>
                    <button type="button" onClick={() => setOrderType('delivery')} className={`p-4 rounded-2xl border-2 text-left transition ${orderType === 'delivery' ? 'border-[#ED2E2E] bg-white shadow-md' : 'border-gray-200 bg-white/50 hover:border-[#ED2E2E]/50'}`}>
                      <Bike className={`mb-2 ${orderType === 'delivery' ? 'text-[#ED2E2E]' : 'text-gray-500'}`} size={22} />
                      <div className="font-bold text-[#0F1C2E]">Delivery</div>
                      <div className="text-xs text-gray-500 mt-1">Powered by Uber Direct.</div>
                    </button>
                  </div>
                </div>

                {/* When? */}
                <div className="border-t border-gray-200 pt-5">
                  <label className="text-sm font-semibold text-[#0F1C2E] mb-3 block flex items-center gap-2"><Clock size={16} className="text-[#ED2E2E]" /> When would you like your order?</label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <button type="button" onClick={() => { setWhen('asap'); setScheduledTime(''); setQuote(null); }} className={`p-4 rounded-2xl border-2 text-left transition ${when === 'asap' ? 'border-[#ED2E2E] bg-white shadow-md' : 'border-gray-200 bg-white/50 hover:border-[#ED2E2E]/50'}`}>
                      <div className="font-bold text-[#0F1C2E]">ASAP</div>
                      <div className="text-xs text-gray-500 mt-1">Ready as soon as possible</div>
                    </button>
                    <button type="button" onClick={() => { setWhen('scheduled'); setQuote(null); }} className={`p-4 rounded-2xl border-2 text-left transition ${when === 'scheduled' ? 'border-[#ED2E2E] bg-white shadow-md' : 'border-gray-200 bg-white/50 hover:border-[#ED2E2E]/50'}`}>
                      <Calendar className={`mb-1 ${when === 'scheduled' ? 'text-[#ED2E2E]' : 'text-gray-500'}`} size={16} />
                      <div className="font-bold text-[#0F1C2E]">Schedule for Later</div>
                      <div className="text-xs text-gray-500 mt-1">Pick a time today</div>
                    </button>
                  </div>
                  {when === 'scheduled' && (
                    timeSlots.length === 0 ? (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">Sorry, we're closed for the day. Try ASAP tomorrow.</div>
                    ) : (
                      <select required value={scheduledTime} onChange={(e) => { setScheduledTime(e.target.value); setQuote(null); }} className="input-c">
                        <option value="">Select a pickup/delivery time…</option>
                        {timeSlots.map((t) => (
                          <option key={t.toISOString()} value={t.toISOString()}>
                            {t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                          </option>
                        ))}
                      </select>
                    )
                  )}
                </div>

                <div className="border-t border-gray-200 pt-5">
                  <h2 className="font-heading font-bold text-lg text-[#0F1C2E] mb-3">Contact</h2>
                  <div>
                    <label className="text-sm font-semibold text-[#0F1C2E]">Full Name *</label>
                    <input required value={form.customerName} onChange={on('customerName')} className="input-c" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-sm font-semibold text-[#0F1C2E]">Phone *</label>
                      <input required value={form.customerPhone} onChange={on('customerPhone')} className="input-c" placeholder="+1 604 xxx xxxx" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#0F1C2E]">Email *</label>
                      <input required type="email" value={form.customerEmail} onChange={on('customerEmail')} className="input-c" />
                    </div>
                  </div>
                </div>

                {orderType === 'delivery' && (
                  <div className="border-t border-gray-200 pt-5">
                    <h2 className="font-heading font-bold text-lg text-[#0F1C2E] mb-3">Delivery Address</h2>
                    <div>
                      <label className="text-sm font-semibold text-[#0F1C2E]">Street Address *</label>
                      <input required value={form.deliveryAddress} onChange={on('deliveryAddress')} className="input-c" placeholder="1234 Main St" />
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <div>
                        <label className="text-sm font-semibold text-[#0F1C2E]">City *</label>
                        <input required value={form.deliveryCity} onChange={on('deliveryCity')} className="input-c" />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#0F1C2E]">Province</label>
                        <input value={form.deliveryState} onChange={on('deliveryState')} className="input-c" />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-[#0F1C2E]">Postal Code *</label>
                        <input required value={form.deliveryPostal} onChange={on('deliveryPostal')} className="input-c" placeholder="V3B 3N6" />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <button type="button" onClick={fetchQuote} disabled={quoteLoading || !form.deliveryAddress || !form.deliveryPostal} className="btn-wrappus text-sm disabled:opacity-50">
                        {quoteLoading ? 'Getting quote...' : quote ? 'Refresh Quote' : 'Get Delivery Quote'}
                      </button>
                      {quote && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-[#ED2E2E] font-bold">${quote.fee.toFixed(2)} {quote.currency}</span>
                          {quote.dropoffEta && <span className="text-gray-500 flex items-center gap-1"><Clock size={14} /> ETA {new Date(quote.dropoffEta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                        </div>
                      )}
                    </div>
                    {quoteError && (
                      <div className="mt-3 flex gap-2 items-start bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                        <AlertCircle size={16} className="shrink-0 mt-0.5" /><span>{quoteError}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div className="border-t border-gray-200 pt-5">
                  <label className="text-sm font-semibold text-[#0F1C2E]">Notes (Optional)</label>
                  <textarea rows={2} value={form.notes} onChange={on('notes')} placeholder={orderType === 'delivery' ? 'Buzzer code, drop-off instructions...' : 'Sauce preference, allergies, etc.'} className="input-c resize-none" />
                </div>

                {/* Referral code (only useful for new customers, but always visible) */}
                {loyaltyBalance && loyaltyBalance.lifetime === 0 && (
                  <div className="border-t border-gray-200 pt-5">
                    <label className="text-sm font-semibold text-[#0F1C2E] flex items-center gap-2"><Gift size={16} className="text-[#ED2E2E]" /> Referral code (optional)</label>
                    <p className="text-xs text-gray-500 mt-1 mb-2">First-time customer? Use a friend's code to send them 50 pts after your first order.</p>
                    <input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="Enter 8-character code" className="input-c uppercase tracking-widest" maxLength={12} />
                  </div>
                )}

                {/* Loyalty rewards */}
                {loyaltyBalance && loyaltyBalance.balance >= 100 && (
                  <div className="border-t border-gray-200 pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Gift size={18} className="text-[#ED2E2E]" />
                        <label className="text-sm font-semibold text-[#0F1C2E]">Loyalty Rewards</label>
                      </div>
                      <div className="text-xs text-gray-500">Balance: <strong className="text-[#ED2E2E]">{loyaltyBalance.balance} pts</strong></div>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Redeem in multiples of 100 (100 pts = $10 off). Max 500 pts per order.</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {[0, 100, 200, 300, 400, 500].map((n) => {
                        const maxOk = n <= Math.min(loyaltyBalance.balance, 500);
                        const disabled = !maxOk;
                        return (
                          <button key={n} type="button" onClick={() => setPointsToRedeem(n)} disabled={disabled}
                            className={`p-2 rounded-xl border-2 text-sm font-semibold transition ${pointsToRedeem === n ? 'border-[#ED2E2E] bg-[#ED2E2E] text-white' : disabled ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed' : 'border-gray-200 bg-white text-[#0F1C2E] hover:border-[#ED2E2E]/50'}`}>
                            {n === 0 ? 'None' : `${n}`}
                          </button>
                        );
                      })}
                    </div>
                    {loyaltyError && <p className="text-xs text-red-600 mt-2">{loyaltyError}</p>}
                    {loyaltyBalance.expiringSoon > 0 && (
                      <p className="text-xs text-orange-600 mt-2">⏰ {loyaltyBalance.expiringSoon} points expire within 30 days.</p>
                    )}
                  </div>
                )}
                {loyaltyBalance && loyaltyBalance.balance < 100 && loyaltyBalance.balance > 0 && (
                  <div className="border-t border-gray-200 pt-5 flex items-center gap-2 text-sm text-gray-600">
                    <Sparkles size={16} className="text-[#ED2E2E]" />
                    You have <strong>{loyaltyBalance.balance} points</strong>. Earn {100 - loyaltyBalance.balance} more to unlock your first $10 reward!
                  </div>
                )}
                {loyaltyBalance && loyaltyBalance.balance === 0 && subtotal >= 1 && (
                  <div className="border-t border-gray-200 pt-5 flex items-center gap-2 text-sm text-gray-600">
                    <Sparkles size={16} className="text-[#ED2E2E]" />
                    You'll earn <strong className="text-[#ED2E2E]">{Math.floor(subtotal)} points</strong> with this order. 100 points = $10 off next time!
                  </div>
                )}

                <button type="submit" disabled={orderType === 'delivery' && !quote} className="btn-wrappus w-full justify-center disabled:opacity-60">
                  Continue to Payment
                </button>
                <style>{`.input-c{width:100%;margin-top:0.35rem;border-radius:0.75rem;border:1px solid #E5E7EB;background:white;padding:0.75rem 1rem;font-size:0.95rem;transition:all 0.2s;} .input-c:focus{outline:none;border-color:#ED2E2E;box-shadow:0 0 0 3px rgba(237,46,46,0.15);} `}</style>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={pay} className="bg-[#FDF3EC] rounded-3xl p-8 space-y-5" data-testid="checkout-payment-step">
                <div className="flex items-center gap-2">
                  <Lock size={16} className="text-[#ED2E2E]" />
                  <h2 className="font-heading font-bold text-2xl text-[#0F1C2E]">Secure Payment</h2>
                </div>
                <p className="text-gray-600 text-sm">
                  You'll finish on Stripe's secure payment page — card, Apple&nbsp;Pay and Google&nbsp;Pay all work.
                  Your card details never touch our servers.
                </p>

                {payError && (
                  <div className="flex gap-2 items-start bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm" data-testid="payment-error">
                    <AlertCircle size={18} className="shrink-0 mt-0.5" /><span>{payError}</span>
                  </div>
                )}

                <div className="bg-white rounded-2xl p-5 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Order</span><span className="font-mono text-xs">{(orderId || '').slice(0, 8).toUpperCase()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">{orderType === 'delivery' ? 'Delivery to' : 'Pickup at'}</span><span className="font-semibold text-right max-w-[60%] truncate">{orderType === 'delivery' ? form.deliveryAddress : 'Coast Meridian Rd'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>${(serverTotals?.subtotal ?? subtotal).toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">GST (5%)</span><span>${(serverTotals?.gst ?? gst).toFixed(2)}</span></div>
                  {(serverTotals?.pst ?? pst) > 0 && (
                    <div className="flex justify-between"><span className="text-gray-600">PST (7%)</span><span>${(serverTotals?.pst ?? pst).toFixed(2)}</span></div>
                  )}
                  {deliveryFee > 0 && <div className="flex justify-between"><span className="text-gray-600">Delivery</span><span>${deliveryFee.toFixed(2)}</span></div>}
                  {loyaltyDiscount > 0 && <div className="flex justify-between text-[#ED2E2E]"><span>Loyalty discount</span><span>-${loyaltyDiscount.toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t border-gray-100 pt-2 font-bold text-base"><span>Amount due</span><span className="text-[#ED2E2E]">${amountDue.toFixed(2)} CAD</span></div>
                </div>

                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-start gap-1.5"><Lock size={12} className="mt-0.5 shrink-0" /> Processed by Stripe — PCI&nbsp;DSS Level 1 certified.</li>
                  <li className="flex items-start gap-1.5"><Lock size={12} className="mt-0.5 shrink-0" /> No billing address typing needed — Stripe handles verification.</li>
                </ul>

                <button type="submit" disabled={paying} className="btn-wrappus w-full justify-center disabled:opacity-60" data-testid="payment-pay-btn">
                  {paying ? 'Redirecting to Stripe…' : `Pay $${amountDue.toFixed(2)}`}
                </button>
                <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-gray-500 hover:text-[#ED2E2E] transition" data-testid="payment-back-btn">
                  Back to order details
                </button>
              </form>
            )}
          </div>

          <aside className="bg-[#FDF3EC] rounded-3xl p-6 h-fit sticky top-28">
            <div className="flex items-center gap-2 mb-4"><ShoppingBag className="text-[#ED2E2E]" size={20} /><h3 className="font-heading font-bold text-lg">Order Summary</h3></div>
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm">
                  <span className="text-gray-700">{i.qty}× {i.name}</span>
                  <span className="font-semibold">${(i.price * i.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1 text-sm pt-4 border-t border-gray-200">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">GST (5%)</span><span>${gst.toFixed(2)}</span></div>
              {pst > 0 && (
                <div className="flex justify-between"><span className="text-gray-600">PST (7%)</span><span>${pst.toFixed(2)}</span></div>
              )}
              {orderType === 'delivery' && quote && (
                <div className="flex justify-between"><span className="text-gray-600">Delivery Fee</span><span>${quote.fee.toFixed(2)}</span></div>
              )}
              {loyaltyDiscount > 0 && (
                <div className="flex justify-between text-[#ED2E2E] font-semibold"><span>Loyalty Discount (-{pointsToRedeem} pts)</span><span>-${loyaltyDiscount.toFixed(2)}</span></div>
              )}
              {when === 'scheduled' && scheduledTime && (
                <div className="flex justify-between pt-2 border-t border-gray-200 mt-2 text-[#ED2E2E] font-semibold"><span>Scheduled</span><span>{new Date(scheduledTime).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span></div>
              )}
              <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-200 mt-2"><span className="text-[#0F1C2E]">Total</span><span className="text-[#ED2E2E]">${grandTotal.toFixed(2)}</span></div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
